/**
 * Single-flight AI-credential re-pull that also repoints a **running** session
 * at a different AI provider config (seat) when the platform hands one back.
 *
 * Three independent triggers converge here — a `credentialRotated` broadcast
 * naming a new seat, any mint result that comes back pinned to a different
 * seat (including the proactive scheduler's), and a driver-reported usage-limit
 * block. They can race, so they coalesce onto one in-flight re-pull.
 *
 * Extracted from `serve.ts` so the restash / restart / coalescing properties
 * are directly testable: `serve.ts` is one 4600-line closure.
 *
 * Spec: skaile-ai/workspaces#567, skaile-ai/platform#2976.
 * @docLink packages/runner/dev-guide#ai-credential-mediation
 */

import type { CredentialMint, CredentialRejection } from "@skaile/workspaces/types";

/** Result of one re-pull, plus whether it moved the session to another seat. */
export interface AiProviderSwitchOutcome {
  /** The mint as returned by the host (already persisted on success). */
  mint: CredentialMint;
  /** True when the session was repointed at a DIFFERENT AI provider config. */
  switched: boolean;
  /** The config id in effect after this re-pull. */
  configId?: string;
}

/** Collaborators the switcher drives. All injected so tests need no session. */
export interface AiProviderConfigSwitcherDeps {
  /**
   * Re-pull the AI credential through the host mediator, rewriting
   * `.credentials.json` on success. `serve.ts` passes
   * `mintAndPersistAiCredential`.
   */
  mint: (args: {
    configId?: string;
    reason: "refresh" | "retry-401";
    rejection?: CredentialRejection;
  }) => Promise<CredentialMint>;
  /** The AI provider config the session is currently pinned to. */
  getConfigId: () => string | undefined;
  /** Repoint the session at `configId` (runner stash + live session config). */
  setConfigId: (configId: string) => void;
  /**
   * Queue a driver restart on the serialized restart chain. MUST be
   * fire-and-forget — see {@link createAiProviderConfigSwitcher}.
   */
  scheduleRestart: (reason: string) => void;
  log: (line: string) => void;
  /**
   * Warn-level sink. A seat switch is the kind of event that has to be
   * reconstructible from a production log, where the floor is WARN
   * (skaile-ai/platform#3540).
   */
  logWarn: (line: string) => void;
}

/** The coalescing re-pull entry point. */
export interface AiProviderConfigSwitcher {
  repull(args: {
    /** Short label naming the trigger, for the log line. */
    trigger: string;
    /** Seat to target; defaults to the currently pinned one. */
    configId?: string;
    /** Mediation reason forwarded to `host.refresh_credential`. */
    reason?: "refresh" | "retry-401";
    /**
     * Which rejection drove a `retry-401`. Only set by a caller that actually
     * classified the failure; it also ranks the coalescing (see
     * {@link createAiProviderConfigSwitcher}).
     */
    rejection?: CredentialRejection;
  }): Promise<AiProviderSwitchOutcome>;
  /**
   * Apply the seat named by an ALREADY-completed mint. For the reactive 401
   * path, whose bounded transient-failure retry wraps the mint itself and so
   * cannot ride {@link repull}.
   *
   * This is the path that self-heals with no broadcast at all: the platform
   * re-resolves a parked seat during an ordinary refresh and simply mints for a
   * peer, and the difference in the result is the only notification there is.
   */
  applyMint(
    mint: CredentialMint,
    args: { trigger: string; requestedConfigId?: string },
  ): AiProviderSwitchOutcome;
}

/**
 * Build the switcher.
 *
 * **The driver restart is scheduled, never awaited.** A restart runs through
 * the driver swap gate, which waits for the active turn to settle — and the
 * limit-block trigger is invoked from *inside* that turn. Awaiting it here
 * would deadlock the very failover it is meant to complete. The driver owns the
 * urgent half anyway: it drops its query and respawns the CLI on the replay,
 * which is what actually clears a resident CLI's cached limit state. The queued
 * restart only brings the live driver's own `aiProviderConfigId` up to date,
 * and can safely land after the turn.
 *
 * **Coalescing covers the re-pull only,** for the same reason: if the shared
 * promise included the restart, a broadcast-triggered switch parked on the
 * active turn would be awaited by a limit failover running inside that turn.
 */
export function createAiProviderConfigSwitcher(
  deps: AiProviderConfigSwitcherDeps,
): AiProviderConfigSwitcher {
  /**
   * The re-pull currently in flight, tagged with the seat it was sent to fetch
   * (`undefined` = "whatever is pinned") and the `reason` it was sent with.
   * Both tags are what make coalescing safe — see
   * {@link AiProviderConfigSwitcher.repull}.
   */
  let inFlight: {
    target: string | undefined;
    reason: "refresh" | "retry-401";
    rejection: CredentialRejection | undefined;
    promise: Promise<AiProviderSwitchOutcome>;
  } | null = null;

  /**
   * How much a re-pull asks for, as a total order. An in-flight re-pull may be
   * adopted only by a request that asks for no more than it does.
   *
   *   0 `refresh` — served straight from the platform's per-config mint cache
   *     while the token is still fresh, without the resolver ever looking at
   *     the seat.
   *   1 `retry-401` with no rejection, or `'auth'` — bypasses that cache and
   *     reaches the resolver. No caller produces this today: `onAuthError`
   *     mints directly rather than through `repull`. It is here because the
   *     wire type admits it and the next caller through this seam will.
   *   2 `retry-401` with `'usage-limit'` — additionally tells the platform the
   *     seat is exhausted rather than the credential rejected, which is the
   *     only thing that re-resolves a config not opted into `autoActivate`
   *     (skaile-ai/platform#3562).
   *
   * Anything not recognised ranks as a plain `retry-401`, matching the
   * "treat an unknown rejection as absent" rule the wire type carries.
   */
  function requestRank(
    reason: "refresh" | "retry-401",
    rejection: CredentialRejection | undefined,
  ): number {
    if (reason !== "retry-401") return 0;
    return rejection === "usage-limit" ? 2 : 1;
  }

  /**
   * Whether an in-flight re-pull asks strongly enough to satisfy a new request.
   *
   * Never the reverse of the rank: letting a limit failover adopt a cached
   * `refresh` would report "same seat" on a lookup that never happened — the
   * exact inertness this module exists to avoid — and spend the driver's one
   * failover retry doing it. The same holds one step up: adopting an `auth`
   * (or bare) `retry-401` would return on a round trip that never told the
   * platform a limit was hit, so the parked seat stays parked.
   */
  function reasonSatisfies(
    pending: { reason: "refresh" | "retry-401"; rejection: CredentialRejection | undefined },
    requested: { reason: "refresh" | "retry-401"; rejection: CredentialRejection | undefined },
  ): boolean {
    return (
      requestRank(pending.reason, pending.rejection) >=
      requestRank(requested.reason, requested.rejection)
    );
  }

  function applyMint(
    mint: CredentialMint,
    args: { trigger: string; requestedConfigId?: string },
  ): AiProviderSwitchOutcome {
    const previous = deps.getConfigId();
    const unchanged: AiProviderSwitchOutcome = {
      mint,
      switched: false,
      ...(previous ? { configId: previous } : {}),
    };
    if (!mint.ok) return unchanged;
    // The mint's own `configId` is authoritative — it names the seat the
    // platform actually minted for, which is not necessarily the one we asked
    // for. Fall back to the requested seat so a platform that does not yet send
    // the field still honours an explicit broadcast target.
    const next = mint.configId ?? args.requestedConfigId ?? previous;
    if (!next || next === previous) return unchanged;
    deps.setConfigId(next);
    deps.logWarn(
      `[serve] AI provider config switched ${previous ?? "none"} -> ${next} (${args.trigger})`,
    );
    deps.scheduleRestart("AI provider config switch");
    return { mint, switched: true, configId: next };
  }

  async function run(args: {
    trigger: string;
    configId?: string;
    reason?: "refresh" | "retry-401";
    rejection?: CredentialRejection;
  }): Promise<AiProviderSwitchOutcome> {
    const mint = await deps.mint({
      ...(args.configId ? { configId: args.configId } : {}),
      reason: args.reason ?? "refresh",
      ...(args.rejection ? { rejection: args.rejection } : {}),
    });
    return applyMint(mint, {
      trigger: args.trigger,
      ...(args.configId ? { requestedConfigId: args.configId } : {}),
    });
  }

  return {
    applyMint,
    repull(args) {
      const pending = inFlight;
      const reason = args.reason ?? "refresh";
      const rejection = args.rejection;
      // Coalesce ONLY onto a re-pull aimed at the same seat AND asking at least
      // as strongly. Adopting a weaker or differently-aimed one is worse than a
      // duplicate round trip: a broadcast naming seat C that landed during a
      // plain refresh would never ask for C, and a limit failover that adopted
      // a cached refresh would report "same seat" without the resolver ever
      // looking. Both report success while leaving the session parked. Anything
      // that cannot be satisfied queues behind instead.
      if (
        pending &&
        pending.target === args.configId &&
        reasonSatisfies(pending, { reason, rejection })
      ) {
        deps.log(`[serve] ${args.trigger}: coalescing into the in-flight ai-credential re-pull`);
        return pending.promise;
      }
      const started = pending
        ? pending.promise.then(
            () => run(args),
            () => run(args),
          )
        : run(args);
      const entry = { target: args.configId, reason, rejection, promise: started };
      inFlight = entry;
      // Clear on settle, not in the caller: coalesced callers await `promise`
      // directly and must not each race to reset the slot.
      return started.finally(() => {
        if (inFlight === entry) inFlight = null;
      });
    },
  };
}
